import React, { useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { usePackagers } from "../../hooks/usePackagers";

const genTempPassword = () => `Gf${Math.random().toString(36).slice(2, 8)}!9`;

export const PackagerManagement = () => {
  const { packagers, create } = usePackagers();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tempPassword, setTempPassword] = useState(genTempPassword());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await create({ name, email, temp_password: tempPassword });
      setShowForm(false);
      setName("");
      setEmail("");
      setTempPassword(genTempPassword());
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create packager");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-extrabold text-2xl tracking-tight">Packagers</h2>
        <button
          onClick={() => setShowForm((s) => !s)}
          data-testid="add-packager-btn"
          className="flex items-center gap-2 rounded-full bg-[#052F24] text-[#DFFF00] px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity"
        >
          <UserPlus size={16} /> Add packager
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5E7EB] p-6 mb-6 grid sm:grid-cols-3 gap-4" data-testid="add-packager-form">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            data-testid="packager-name-input"
            className="rounded-xl border border-[#E5E7EB] px-4 py-2.5 text-sm outline-none focus:border-[#052F24]/40"
            required
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="Email"
            data-testid="packager-email-input"
            className="rounded-xl border border-[#E5E7EB] px-4 py-2.5 text-sm outline-none focus:border-[#052F24]/40"
            required
          />
          <input
            value={tempPassword}
            onChange={(e) => setTempPassword(e.target.value)}
            placeholder="Temporary password"
            data-testid="packager-temp-password-input"
            className="rounded-xl border border-[#E5E7EB] px-4 py-2.5 text-sm outline-none focus:border-[#052F24]/40"
            required
          />
          {error && <p className="text-sm text-red-600 sm:col-span-3" data-testid="packager-form-error">{error}</p>}
          <button
            type="submit"
            disabled={saving}
            data-testid="packager-form-submit"
            className="sm:col-span-3 flex items-center justify-center gap-2 bg-[#052F24] text-[#DFFF00] font-bold py-2.5 rounded-full disabled:opacity-40"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Create packager account"}
          </button>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {!packagers ? (
          <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-[#052F24]/40" /></div>
        ) : packagers.length === 0 ? (
          <div className="p-16 text-center text-[#6B7280]" data-testid="packagers-empty-state">No packagers yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="packagers-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-left text-[11px] font-bold tracking-[0.12em] uppercase text-[#6B7280]">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {packagers.map((p) => (
                <tr key={p.id} className="border-t border-[#E5E7EB]" data-testid={`packager-row-${p.id}`}>
                  <td className="px-5 py-4 font-semibold">{p.name}</td>
                  <td className="px-5 py-4 text-[#6B7280]">{p.email}</td>
                  <td className="px-5 py-4">
                    {p.must_change_password ? (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-2.5 py-1">Awaiting first login</span>
                    ) : (
                      <span className="text-[10px] font-bold text-[#052F24] bg-[#DFFF00]/60 rounded-full px-2.5 py-1">Active</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-[#6B7280]">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
